import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { VUKZ } from '../theme/vukzTheme';
import { useSportsLang } from '../contexts/SportsLangContext';
import { listSessions, cancelSession, reprocessSession } from '../services/trackVisionService';
import {
  CheckCircle2, Clock, AlertTriangle, XCircle,
  Video, ArrowLeft, ChevronLeft, ChevronRight,
  RefreshCw, Loader2, Ban, Filter, Footprints, BarChart3, Eye,
} from 'lucide-react';

const T = {
  bg: '#fafafa', card: '#ffffff', text: '#111111', textSoft: '#6b7280',
  textMute: '#9ca3af', border: '#e5e7eb', accent: '#111111', accentSoft: '#f3f4f6',
  success: '#16a34a', successBg: '#f0fdf4', warning: '#f59e0b', warningBg: '#fffbeb',
  error: '#dc2626', errorBg: '#fef2f2', info: '#2563eb', infoBg: '#eff6ff',
};

const EVENT_LABELS = {
  sprint: 'Sprint', gait: 'Marcha', jump: 'Salto', run: 'Corrida',
  hurdles: 'Barreiras', unknown: 'Auto', auto: 'Auto',
};

const LABELS_I18N = {
  pt: {
    status: {
      completed: 'Concluída', processing: 'Processando', pending: 'Na fila',
      uploaded: 'Enviado', queued: 'Na fila', failed: 'Falha', cancelled: 'Cancelada',
    },
    title: 'Sessões TrackVision',
    total: (n) => `${n} sessões no total`,
    loading: 'Carregando...',
    newAnalysis: 'Nova Análise',
    allStatuses: 'Todos os status',
    statusCompleted: 'Concluídas', statusProcessing: 'Processando',
    statusPending: 'Na fila', statusFailed: 'Falhas', statusCancelled: 'Canceladas',
    allEvents: 'Todos os eventos',
    eventGait: 'Marcha', eventRun: 'Corrida', eventJump: 'Salto', eventHurdles: 'Barreiras',
    clearFilters: 'Limpar filtros',
    loadingSessions: 'Carregando sessões...',
    noSessions: 'Nenhuma sessão encontrada',
    confirmCancel: (id) => `Cancelar sessão #${id}?`,
    confirmReprocess: (id) => `Reprocessar sessão #${id}?`,
    errPrefix: 'Erro',
    reprocess: 'Reprocessar', cancel: 'Cancelar', viewResult: 'Ver resultado',
  },
  en: {
    status: {
      completed: 'Completed', processing: 'Processing', pending: 'Queued',
      uploaded: 'Uploaded', queued: 'Queued', failed: 'Failed', cancelled: 'Cancelled',
    },
    title: 'TrackVision Sessions',
    total: (n) => `${n} sessions total`,
    loading: 'Loading...',
    newAnalysis: 'New Analysis',
    allStatuses: 'All statuses',
    statusCompleted: 'Completed', statusProcessing: 'Processing',
    statusPending: 'Queued', statusFailed: 'Failed', statusCancelled: 'Cancelled',
    allEvents: 'All events',
    eventGait: 'Gait', eventRun: 'Run', eventJump: 'Jump', eventHurdles: 'Hurdles',
    clearFilters: 'Clear filters',
    loadingSessions: 'Loading sessions...',
    noSessions: 'No sessions found',
    confirmCancel: (id) => `Cancel session #${id}?`,
    confirmReprocess: (id) => `Reprocess session #${id}?`,
    errPrefix: 'Error',
    reprocess: 'Reprocess', cancel: 'Cancel', viewResult: 'View result',
  },
};

const PAGE_SIZE = 30;

const SessionRow = ({ s, index, offset, onCancel, onReprocess }) => {
  const { lang } = useSportsLang();
  const L = LABELS_I18N[lang] || LABELS_I18N.pt;
  const STATUS_META = {
    completed: { icon: CheckCircle2, color: T.success, bg: T.successBg, label: L.status.completed },
    processing: { icon: Loader2, color: T.info, bg: T.infoBg, label: L.status.processing },
    pending:    { icon: Clock, color: T.warning, bg: T.warningBg, label: L.status.pending },
    uploaded:   { icon: Clock, color: T.warning, bg: T.warningBg, label: L.status.uploaded },
    queued:     { icon: Clock, color: T.warning, bg: T.warningBg, label: L.status.queued },
    failed:     { icon: XCircle, color: T.error, bg: T.errorBg, label: L.status.failed },
    cancelled:  { icon: Ban, color: T.textMute, bg: T.accentSoft, label: L.status.cancelled },
  };
  const meta = STATUS_META[s.status] || STATUS_META.pending;
  const StatusIcon = meta.icon;
  const canCancel = s.status === 'pending' || s.status === 'processing' || s.status === 'uploaded' || s.status === 'queued';
  const canReprocess = s.status === 'failed' || s.status === 'cancelled';

  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b"
      style={{ borderColor: T.border }}>
      <span className="text-[10px] font-bold w-6 text-center" style={{ color: T.textMute }}>
        {offset + index + 1}
      </span>
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: meta.bg }}>
        <StatusIcon size={14} style={{ color: meta.color }} className={s.status === 'processing' ? 'animate-spin' : ''} />
      </div>
      <Link to={`/track-vision/sessions/${s.id}`} className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold truncate" style={{ color: T.text }}>#{s.id}</span>
          {s.event_type && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-gray-100"
              style={{ color: T.textSoft }}>
              {EVENT_LABELS[s.event_type] || s.event_type}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-[10px] truncate" style={{ color: T.textMute }}>
            {s.video_filename ? s.video_filename.substring(0, 50) : '—'}
          </span>
          {s.created_at && (
            <span className="text-[10px] shrink-0" style={{ color: T.textMute }}>
              {new Date(s.created_at).toLocaleDateString('pt-BR')}
            </span>
          )}
        </div>
      </Link>
      <div className="text-right shrink-0 min-w-[60px]">
        {s.movement_quality_score != null && (
          <div className="text-xs font-bold" style={{ color: T.text }}>
            {s.movement_quality_score.toFixed(0)} <span style={{ color: T.textMute }}>pts</span>
          </div>
        )}
        {s.processing_time_ms != null && (
          <div className="text-[10px]" style={{ color: T.textMute }}>
            {(s.processing_time_ms / 1000).toFixed(1)}s
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {canReprocess && (
          <button onClick={() => onReprocess(s.id)} className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors" title={L.reprocess}>
            <RefreshCw size={14} style={{ color: T.accent }} />
          </button>
        )}
        {canCancel && (
          <button onClick={() => onCancel(s.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title={L.cancel}>
            <Ban size={14} style={{ color: T.error }} />
          </button>
        )}
        {s.status === 'completed' && (
          <Link to={`/track-vision/sessions/${s.id}`} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title={L.viewResult}>
            <BarChart3 size={14} style={{ color: T.accent }} />
          </Link>
        )}
        <ChevronRight size={14} style={{ color: T.textMute }} />
      </div>
    </div>
  );
};

export default function TrackVisionSessionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang } = useSportsLang();
  const L = LABELS_I18N[lang] || LABELS_I18N.pt;

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const filterStatus = searchParams.get('status') || '';
  const filterEvent = searchParams.get('event_type') || '';

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const offset = (currentPage - 1) * PAGE_SIZE;

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { per_page: PAGE_SIZE, page: currentPage };
      if (filterStatus) params.status = filterStatus;
      if (filterEvent) params.event_type = filterEvent;
      const resp = await listSessions(params);
      setSessions(resp.data || []);
      setTotal(resp.total || 0);
    } catch (err) {
      console.error('[TrackVision Sessions] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterStatus, filterEvent]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const goToPage = (p) => { const params = new URLSearchParams(searchParams); params.set('page', String(p)); setSearchParams(params); };
  const setFilter = (key, value) => { const params = new URLSearchParams(searchParams); if (value) params.set(key, value); else params.delete(key); params.set('page', '1'); setSearchParams(params); };

  const handleCancel = async (id) => {
    if (!window.confirm(L.confirmCancel(id))) return;
    try { await cancelSession(id); await fetchSessions(); } catch (err) { alert(`${L.errPrefix}: ${err.message}`); }
  };
  const handleReprocess = async (id) => {
    if (!window.confirm(L.confirmReprocess(id))) return;
    try { await reprocessSession(id); await fetchSessions(); } catch (err) { alert(`${L.errPrefix}: ${err.message}`); }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: T.bg }}>
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/track-vision')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft size={18} style={{ color: T.text }} />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Footprints size={18} style={{ color: T.accent }} />
              <h1 className="text-xl font-black" style={{ color: T.text }}>{L.title}</h1>
            </div>
            <p className="text-xs" style={{ color: T.textSoft }}>{total > 0 ? L.total(total) : L.loading}</p>
          </div>
          <Link to="/track-vision" className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: T.accent }}>
            <Video size={14} /> {L.newAnalysis}
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border p-3 mb-4 flex flex-wrap items-center gap-3" style={{ borderColor: T.border }}>
          <Filter size={14} style={{ color: T.textSoft }} />
          <select value={filterStatus} onChange={(e) => setFilter('status', e.target.value)}
            className="text-xs px-3 py-1.5 rounded-lg border" style={{ borderColor: T.border, color: T.text }}>
            <option value="">{L.allStatuses}</option>
            <option value="completed">{L.statusCompleted}</option>
            <option value="processing">{L.statusProcessing}</option>
            <option value="pending">{L.statusPending}</option>
            <option value="failed">{L.statusFailed}</option>
            <option value="cancelled">{L.statusCancelled}</option>
          </select>
          <select value={filterEvent} onChange={(e) => setFilter('event_type', e.target.value)}
            className="text-xs px-3 py-1.5 rounded-lg border" style={{ borderColor: T.border, color: T.text }}>
            <option value="">{L.allEvents}</option>
            <option value="sprint">Sprint</option>
            <option value="gait">{L.eventGait}</option>
            <option value="run">{L.eventRun}</option>
            <option value="jump">{L.eventJump}</option>
            <option value="hurdles">{L.eventHurdles}</option>
          </select>
          {(filterStatus || filterEvent) && (
            <button onClick={() => { setSearchParams(new URLSearchParams({ page: '1' })); }}
              className="text-[10px] font-bold px-2 py-1 rounded hover:bg-gray-100" style={{ color: T.error }}>
              {L.clearFilters}
            </button>
          )}
        </div>

        {/* List */}
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: T.border }}>
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 size={24} className="animate-spin mx-auto mb-2" style={{ color: T.accent }} />
              <p className="text-xs" style={{ color: T.textSoft }}>{L.loadingSessions}</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="p-12 text-center">
              <Video size={24} style={{ color: T.textMute }} className="mx-auto mb-2" />
              <p className="text-xs font-semibold" style={{ color: T.textSoft }}>{L.noSessions}</p>
            </div>
          ) : (
            <div>
              {sessions.map((s, i) => (
                <SessionRow key={s.id} s={s} index={i} offset={offset} onCancel={handleCancel} onReprocess={handleReprocess} />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}
              className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-30" style={{ borderColor: T.border }}>
              <ChevronLeft size={16} style={{ color: T.text }} />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let page;
                if (totalPages <= 7) page = i + 1;
                else if (currentPage <= 4) page = i + 1;
                else if (currentPage >= totalPages - 3) page = totalPages - 6 + i;
                else page = currentPage - 3 + i;
                return (
                  <button key={page} onClick={() => goToPage(page)}
                    className="w-8 h-8 rounded-lg text-xs font-bold transition-colors"
                    style={{ backgroundColor: page === currentPage ? T.accent : 'transparent', color: page === currentPage ? '#fff' : T.textSoft }}>
                    {page}
                  </button>
                );
              })}
            </div>
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages}
              className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-30" style={{ borderColor: T.border }}>
              <ChevronRight size={16} style={{ color: T.text }} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
